import { Controller } from "@hotwired/stimulus"
import { TempFormHelper } from "~/helpers/temp_form_helper"

// This controller handles the deletion and cancellation of recipients in the campaign audience
export default class extends Controller {
  static get values() {
    return {
      selectedEmails: { type: Array, default: [] },
      campaignId: { type: Number, default: 0 },
      allSelected: { type: Boolean, default: false },
      audienceUrl: { type: String, default: "" },
      audienceCancelUrl: { type: String, default: "" },
    }
  }

  // This function is triggered when the "Delete" button is clicked for multiple recipients
  deleteRecipients() {
    const isAllSelected = this.allSelectedValue

    // Only show error when neither all selected nor individual emails are selected
    if (!isAllSelected && this.selectedEmailsValue.length === 0) {
      App.displayFlash("danger", i18n.t("js.back.campaigns.audience.no_recipients_to_remove"))
      return
    }

    let formToSubmit = TempFormHelper.create(this.audienceUrlValue, "delete", (form) => {
      if (isAllSelected) {
        // Send all_selected flag for bulk deletion using current filters
        TempFormHelper.addHiddenInput(form, "all_selected", "true")

        // Add current filter params to the form
        const currentParams = this.getCurrentParams()
        for (const [key, value] of Object.entries(currentParams)) {
          if (Array.isArray(value)) {
            value.forEach((v) => TempFormHelper.addHiddenInput(form, key + "[]", v))
          } else {
            TempFormHelper.addHiddenInput(form, key, value)
          }
        }
      } else {
        // Regular case - adding selected emails individually
        this.selectedEmailsValue.forEach((email) => {
          TempFormHelper.addHiddenInput(form, "campaign[recipients][]", email)
        })
      }
    })

    const deleteButton = this.element.querySelector('[data-action*="campaign--audience--delete#deleteRecipients"]')
    if (deleteButton) {
      deleteButton.disabled = true
      deleteButton.innerHTML = `<div class='far fa-spinner-third fa-spin'></div> ${i18n.t("js.back.campaigns.audience.removing")}`
    }

    formToSubmit.addEventListener("turbo:submit-end", () => {
      if (deleteButton) {
        deleteButton.disabled = false
        deleteButton.innerHTML = `<span class="far fa-trash-alt" aria-hidden="true"></span> ${i18n.t("remove").capitalize}`
      }
    })

    formToSubmit.requestSubmit()
  }

  // This function is triggered when the "Cancel" button is clicked for multiple recipients
  cancelRecipients() {
    const isAllSelected = this.allSelectedValue

    // Only show error when neither all selected nor individual emails are selected
    if (!isAllSelected && this.selectedEmailsValue.length === 0) {
      App.displayFlash("danger", i18n.t("js.back.campaigns.audience.no_recipients_to_cancel"))
      return
    }

    let formToSubmit = TempFormHelper.create(this.audienceCancelUrlValue, "post", (form) => {
      if (isAllSelected) {
        // Send all_selected flag for bulk cancellation using current filters
        TempFormHelper.addHiddenInput(form, "all_selected", "true")

        // Add current filter params to the form
        const currentParams = this.getCurrentParams()
        for (const [key, value] of Object.entries(currentParams)) {
          if (Array.isArray(value)) {
            value.forEach((v) => TempFormHelper.addHiddenInput(form, key + "[]", v))
          } else {
            TempFormHelper.addHiddenInput(form, key, value)
          }
        }
      } else {
        // Regular case - adding selected emails individually
        this.selectedEmailsValue.forEach((email) => {
          TempFormHelper.addHiddenInput(form, "campaign[recipients][]", email)
        })
      }
    })

    const cancelButton = this.element.querySelector('[data-action*="campaign--audience--delete#cancelRecipients"]')
    if (cancelButton) {
      cancelButton.disabled = true
      cancelButton.innerHTML = `<div class='far fa-spinner-third fa-spin'></div> ${i18n.t("js.back.campaigns.audience.canceling")}`
    }

    formToSubmit.addEventListener("turbo:submit-end", () => {
      if (cancelButton) {
        cancelButton.disabled = false
        cancelButton.innerHTML = `<span class="far fa-minus-octagon" aria-hidden="true"></span> ${i18n.t("js.back.campaigns.audience.cancel_pending_emails")}`
      }
    })

    formToSubmit.requestSubmit()
  }

  // Delete a single recipient when the trash icon is clicked in the table row
  deleteRecipient(event) {
    const email = event.params.email

    if (window.confirm(i18n.t("js.back.campaigns.audience.confirm_remove_recipient", { email: email }))) {
      let formToSubmit = TempFormHelper.create(this.audienceUrlValue, "delete", (form) => {
        TempFormHelper.addHiddenInput(form, "campaign[recipients][]", email)
      })

      formToSubmit.requestSubmit()
    }
  }

  updateSelection(event) {
    if (event.detail) {
      // Store whether all items are selected
      this.allSelectedValue = !!event.detail.allSelected

      if (event.detail.dataSets) {
        this.selectedEmailsValue = event.detail.dataSets
          .filter((dataSet) => dataSet.recipientEmail)
          .map((dataSet) => dataSet.recipientEmail)
      } else {
        this.selectedEmailsValue = []
      }
    } else {
      this.allSelectedValue = false
      this.selectedEmailsValue = []
    }
  }

  // Helper method to get current filter parameters from the URL
  getCurrentParams() {
    const urlParams = new URLSearchParams(window.location.search)
    const params = {}

    for (const [key, value] of urlParams.entries()) {
      // Handle array parameters (with [] notation)
      if (key.endsWith("[]")) {
        const baseKey = key.slice(0, -2)
        if (!params[baseKey]) params[baseKey] = []
        params[baseKey].push(value)
      } else {
        params[key] = value
      }
    }

    return params
  }
}
